<template>
  <v-menu
    v-model="dateMenu"
    min-width="auto"
    :close-on-content-click="false"
    right
    bottom
    nudge-bottom="50">
    <template v-slot:activator="{ on }">
      <v-text-field
        v-bind="$attrs"
        ref="dateInput"
        outlined
        :value="formattedDateForInput"
        @input="onInput"
        @blur="onBlur"
        :disabled="disabled"
        @click:append="dateMenu = !dateMenu"
        v-on="on"
        @click:clear="clearDate()"
        :rules="rules"
        append-icon="mdi-calendar"
        placeholder="MM/DD/YYYY"
        :label="label"
        :clearable="clearable"></v-text-field>
    </template>
    <v-date-picker
      v-model="dateForPicker"
      @input="allowRange ? null : (dateMenu = false)"
      :min="minimumDate"
      :max="maximumDate"
      :range="allowRange"
      :selected-items-text="formattedDateRange">
      <v-row dense v-if="allowRange">
        <v-col>
          <EsButtonCancel @click="clearRange()" block />
        </v-col>
        <v-col>
          <EsButtonSave @click="saveRange()" block />
        </v-col>
      </v-row>
    </v-date-picker>
  </v-menu>
</template>

<script>
import dayjs from 'dayjs';
import { EsButtonCancel, EsButtonSave } from '@86-repairs/design-system';

export default {
  name: 'EsDatePicker',
  components: {
    EsButtonCancel,
    EsButtonSave,
  },
  data: () => ({
    dateMenu: false,
    dateToSave: null,
    unsavedRange: null,
    dateToValidate: null,
    requiredRule: (v) => !!v || 'Required',
  }),
  props: {
    initialDate: {
      type: [Date, String, Array],
      default: null,
    },
    minDate: {
      type: Date,
      default: null,
    },
    maxDate: {
      type: [Date, Object],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    returnDateOnly: {
      type: Boolean,
      default: false,
    },
    allowRange: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dateForPicker: {
      get() {
        if (this.allowRange) {
          return this.unsavedRange;
        }
        return dayjs(this.initialDate || new Date()).format('YYYY-MM-DD');
      },
      set(value) {
        if (this.allowRange) {
          this.unsavedRange = value;
        } else {
          this.setDateToSave(value);
        }
      },
    },
    formattedDateForInput() {
      if (this.dateToSave != null) {
        if (this.allowRange) {
          return `${dayjs(this.dateToSave[0]).format('MM/DD/YYYY')} ${
            this.dateToSave[1]
              ? `- ${dayjs(this.dateToSave[1]).format('MM/DD/YYYY')}`
              : ''
          }`;
        }
        return dayjs(this.dateToSave).format('MM/DD/YYYY');
      }
      if (this.allowRange) {
        return this.initialDate
          ? `${dayjs(this.initialDate[0]).format('MM/DD/YYYY')} ${
              this.initialDate[1]
                ? `- ${dayjs(this.initialDate[1]).format('MM/DD/YYYY')}`
                : ''
            }`
          : null;
      }
      return this.initialDate
        ? dayjs(this.initialDate).format('MM/DD/YYYY')
        : null;
    },
    formattedDateRange() {
      const sortedDates = this.unsavedRange
        ? [...this.unsavedRange].sort()
        : null;
      return `${dayjs((sortedDates || [])[0]).format('MMM D')} - ${dayjs(
        (sortedDates || [])[1],
      ).format('MMM D')}`;
    },
    rules() {
      const rules = [];
      if (this.required) {
        rules.push(this.requiredRule);
      }
      return rules;
    },
    minimumDate() {
      return this.minDate ? dayjs(this.minDate).format('YYYY-MM-DD') : null;
    },
    maximumDate() {
      return this.maxDate ? dayjs(this.maxDate).format('YYYY-MM-DD') : null;
    },
  },
  mounted() {
    if (this.allowRange) {
      this.unsavedRange = this.initialDate;
    }
  },
  watch: {
    initialDate(value) {
      if (Array.isArray(value)) {
        if (value[1] && value[0] !== value[1]) {
          this.unsavedRange = value;
          this.dateToSave = [
            dayjs(value[0]).format('YYYY-MM-DD'),
            dayjs(value[1]).format('YYYY-MM-DD'),
          ];
        } else {
          this.unsavedRange = [value[0]];
          this.dateToSave = [dayjs(value[0]).format('YYYY-MM-DD')];
        }
      } else {
        this.dateToSave = dayjs(value).format('YYYY-MM-DD');
      }
    },
  },
  methods: {
    onInput(val) {
      this.dateToValidate = val;
    },
    onBlur() {
      if (this.dateToValidate) {
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(this.dateToValidate)) {
          const newDate = dayjs(this.dateToValidate);
          const newDateFormatted = newDate.format('YYYY-MM-DD');
          const minDateFormatted = dayjs(this.minDate).format('YYYY-MM-DD');
          const maxDateFormatted = dayjs(this.maxDate).format('YYYY-MM-DD');
          if (newDate.isValid()) {
            if (this.minDate && this.maxDate) {
              if (
                newDateFormatted >= minDateFormatted &&
                newDateFormatted <= maxDateFormatted
              ) {
                this.setDateToSave(newDate);
              } else {
                this.showError(
                  `Date must be after ${dayjs(this.minDate).format(
                    'MM/DD/YYYY',
                  )} and before ${dayjs(this.maxDate).format('MM/DD/YYYY')}`,
                );
              }
            } else if (this.minDate) {
              if (newDateFormatted >= minDateFormatted) {
                this.setDateToSave(newDate);
              } else {
                this.showError(
                  `Date must be after ${dayjs(this.minDate).format(
                    'MM/DD/YYYY',
                  )}`,
                );
              }
            } else if (this.maxDate) {
              if (newDateFormatted <= maxDateFormatted) {
                this.setDateToSave(newDate);
              } else {
                this.showError(
                  `Date must be before ${dayjs(this.maxDate).format(
                    'MM/DD/YYYY',
                  )}`,
                );
              }
            } else {
              this.setDateToSave(newDate);
            }
          } else {
            this.showError('Invalid date');
          }
        } else {
          this.showError('Invalid date format');
        }
      }
    },
    setDateToSave(date) {
      let formattedDate;
      if (this.allowRange) {
        formattedDate = date ? date.sort() : null;
      } else {
        formattedDate = date
          ? this.returnDateOnly
            ? dayjs(date).format('YYYY-MM-DD')
            : dayjs(date).format()
          : null;
      }
      this.dateToSave = formattedDate;
      this.$emit('setDate', formattedDate);
    },
    showError(msg) {
      this.$refs.dateInput.reset();
      this.dateToSave = null;
      this.$emit('showError', msg);
    },
    saveRange() {
      this.setDateToSave(this.unsavedRange);
      this.dateMenu = false;
    },
    clearRange() {
      this.unsavedRange = null;
      this.dateMenu = false;
    },
    clearDate() {
      if (this.allowRange) {
        this.clearRange();
      }
      this.setDateToSave(null);
    },
  },
};
</script>
